// Sector calendars — holiday periods no API publishes (bouwverlof, bouwvak, …), entered
// by a Storalytic super-admin and shared by every store that follows the calendar.
// Pure, framework-free (unit-tested without mounting anything). The backend's yearly
// reminder mail follows the same rules (coach/sector_calendar_reminder.py).
//
// An entry is one period: { HolidayCalendarEntryId, CalendarKey, CountryCode, Name,
// StartDate, EndDate }. Name is "<calendar title> — <period>", e.g.
// "Bouwverlof West- & Oost-Vlaanderen — Summer". A calendar's PERIOD (Summer, Winter)
// recurs yearly: that is the unit a reminder is about.

export const REMINDER_LEAD_DAYS = 90   // start reminding 3 months before last year's start

const SEP = ' — '
const DAY = 86400000

const toDate = (iso) => new Date(`${iso}T00:00:00Z`)
const iso = (d) => d.toISOString().slice(0, 10)
const addDays = (isoStr, n) => iso(new Date(toDate(isoStr).getTime() + n * DAY))
const addYear = (isoStr) => {
  const d = toDate(isoStr)
  d.setUTCFullYear(d.getUTCFullYear() + 1)
  return iso(d)
}

export function splitName(name, key) {
  const i = (name || '').lastIndexOf(SEP)
  return i > 0 ? { title: name.slice(0, i), period: name.slice(i + SEP.length) }
    : { title: key, period: name || key }
}

// The state of one recurring period, from its entries. The latest entry L says when the
// next one is expected (L + 1 year); from REMINDER_LEAD_DAYS before that the next year
// is DUE, and once its expected start has passed it is OVERDUE. Entering it makes it the
// new latest, which moves everything a year on.
export function periodStatus(entries, today) {
  const sorted = [...entries].sort((a, b) => a.StartDate.localeCompare(b.StartDate))
  const last = sorted[sorted.length - 1]
  const expectedStart = addYear(last.StartDate)
  const expectedEnd = addYear(last.EndDate)
  const reminderFrom = addDays(expectedStart, -REMINDER_LEAD_DAYS)
  const state = today >= expectedStart ? 'overdue' : today >= reminderFrom ? 'due' : 'ok'
  return { state, last, expectedStart, expectedEnd, reminderFrom, entries: sorted }
}

export function groupCalendars(entries, today) {
  const byKey = new Map()
  for (const e of entries) {
    const { title, period } = splitName(e.Name, e.CalendarKey)
    if (!byKey.has(e.CalendarKey)) {
      byKey.set(e.CalendarKey, { key: e.CalendarKey, country: e.CountryCode, title, periods: new Map() })
    }
    const cal = byKey.get(e.CalendarKey)
    if (!cal.periods.has(period)) cal.periods.set(period, [])
    cal.periods.get(period).push(e)
  }
  return [...byKey.values()]
    .map((c) => ({
      ...c,
      periods: [...c.periods.entries()]
        .map(([period, es]) => ({ period, ...periodStatus(es, today) }))
        .sort((a, b) => a.expectedStart.localeCompare(b.expectedStart)),
    }))
    .map((c) => ({ ...c, state: worst(c.periods.map((p) => p.state)) }))
    .sort((a, b) => a.title.localeCompare(b.title))
}

const ORDER = { overdue: 0, due: 1, ok: 2 }
const worst = (states) => states.sort((a, b) => ORDER[a] - ORDER[b])[0] || 'ok'

// Month names per language. `lang` defaults to English: the reminder mail below goes to
// Storalytic staff only and stays English (docs/reference/dutch_glossary.md §15); the
// super-admin panel passes the portal language.
const MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  nl: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
}
export function fmtDate(isoStr, withYear = true, lang = 'en') {
  const d = toDate(isoStr)
  const months = MONTHS[lang] || MONTHS.en
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ''}`
}

export function fmtRange(start, end, lang = 'en') {
  const sameYear = start.slice(0, 4) === end.slice(0, 4)
  return `${fmtDate(start, !sameYear, lang)} – ${fmtDate(end, true, lang)}`
}

// "2026" for a summer, "2026–27" for a period that crosses New Year.
export function seasonLabel(start, end) {
  const a = start.slice(0, 4)
  const b = end.slice(0, 4)
  return a === b ? a : `${a}–${b.slice(2)}`
}

// The weekly reminder mail for the super-admins (one per calendar with due/overdue
// periods). `stores` = names of the stores that follow the calendar.
export function reminderMail(cal, stores) {
  const open = cal.periods.filter((p) => p.state !== 'ok')
  if (!open.length) return null
  const first = open[0]
  const season = seasonLabel(first.expectedStart, first.expectedEnd)
  const subject = `Storalytic: enter ${cal.title} — ${first.period.toLowerCase()} ${season}`
  const lines = open.map((p) => {
    const s = seasonLabel(p.expectedStart, p.expectedEnd)
    const when = p.state === 'overdue'
      ? `was expected from ${fmtDate(p.expectedStart)} and is still missing`
      : `is expected around ${fmtDate(p.expectedStart)}`
    return `• ${p.period} ${s} ${when} (last year: ${fmtRange(p.last.StartDate, p.last.EndDate)}).`
  })
  const who = stores.length
    ? `${stores.length} ${stores.length === 1 ? 'store follows' : 'stores follow'} this calendar: ${stores.join(', ')}.`
    : 'No store follows this calendar yet.'
  return {
    subject,
    body: [
      `${cal.title} hasn't been entered for the coming period yet:`,
      ...lines,
      who,
      'Enter the dates under Sector calendars on the company-switch page. Until then these days count as ordinary days for those stores. '
        + 'This reminder comes every Monday until the dates are in.',
    ],
  }
}
